import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Leaf, Users, Sun, Heart, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const OLIVE_GROVE_IMAGE = "https://customer-assets.emergentagent.com/job_2ad418ae-721e-40fd-824f-e8019fae87b8/artifacts/macjzshp_image.png";
const HANDS_IMAGE = "https://images.unsplash.com/photo-1597868165956-03a6827955b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxvcmdhbmljJTIwZmFybSUyMHZlZ2V0YWJsZXMlMjBoYXJ2ZXN0JTIwaGFuZHMlMjBzb2lsfGVufDB8fHx8MTc3MTYyNDE2N3ww&ixlib=rb-4.1.0&q=85";
const FAMILY_IMAGE = "https://customer-assets.emergentagent.com/job_2ad418ae-721e-40fd-824f-e8019fae87b8/artifacts/macjzshp_image.png";

// Team member images
const TEAM_MANUELA = "https://customer-assets.emergentagent.com/job_2ad418ae-721e-40fd-824f-e8019fae87b8/artifacts/pgo8kjcj_image.png";
const TEAM_JOSE = "https://customer-assets.emergentagent.com/job_2ad418ae-721e-40fd-824f-e8019fae87b8/artifacts/b23rcq4k_IMG-20230828-WA0000.jpg";
const TEAM_ANGELINA = "https://customer-assets.emergentagent.com/job_2ad418ae-721e-40fd-824f-e8019fae87b8/artifacts/ops2gtow_IMG-20231002-WA0000.jpg";
const TEAM_MARIA = "https://customer-assets.emergentagent.com/job_2ad418ae-721e-40fd-824f-e8019fae87b8/artifacts/qs95tu6n_20230221_161143.jpg";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const teamMembers = [
  {
    name: "Manuela Oliveira",
    role: "Fundadora",
    description: "A criadora da Quinta da Oliveirinha. Com visão e dedicação, transformou o sonho de uma agricultura sustentável em realidade.",
    image: TEAM_MANUELA
  },
  {
    name: "Sr. José",
    role: "Agricultor",
    description: "Pai da Manuela e avô da Maria Leonor. Décadas de experiência na terra, dedicado a 100% à quinta.",
    image: TEAM_JOSE
  },
  {
    name: "Dona Angelina",
    role: "Agricultora",
    description: "Mãe da Manuela e avó da Maria Leonor. O seu conhecimento tradicional é essencial para a qualidade dos nossos produtos.",
    image: TEAM_ANGELINA,
    imagePosition: "object-top"
  },
  {
    name: "Maria Leonor",
    role: "A Oliveirinha",
    description: "O elemento mais novo da Quinta da Oliveirinha. A próxima geração que já cresce a amar a terra.",
    image: TEAM_MARIA
  }
];

const philosophy = [
  {
    icon: Leaf,
    title: "Agricultura Sustentável",
    description: "Utilizamos métodos que respeitam os ciclos naturais, minimizando o impacto ambiental e preservando a biodiversidade da nossa região."
  },
  {
    icon: Users,
    title: "Projeto Familiar",
    description: "Somos uma família unida pelo amor à terra. Cada membro contribui para que os nossos produtos cheguem frescos e com qualidade."
  },
  {
    icon: Sun,
    title: "Produção Local",
    description: "Valorizamos a produção local e os circuitos curtos. Os nossos produtos viajam poucos quilómetros até à sua mesa."
  },
  {
    icon: Heart,
    title: "Paixão pela Terra",
    description: "Mais do que uma atividade, a agricultura é a nossa paixão. Cultivamos com dedicação e entregamos com orgulho."
  }
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src={OLIVE_GROVE_IMAGE}
            alt="Campo da Quinta da Oliveirinha"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#3A4A38]/90 via-[#3A4A38]/70 to-[#3A4A38]/50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl text-white"
          >
            <span className="font-accent text-2xl text-[#D4C4A8]">
              A Nossa História
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mt-2 mb-6">
              Sobre a Quinta da Oliveirinha
            </h1>
            <p className="text-lg text-white/80">
              Uma história de família, tradição e amor pela agricultura portuguesa.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-accent text-2xl text-[#1B93A4]">
                Quem Somos
              </span>
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2 mb-6">
                Uma família ligada à terra
              </h2>
              <div className="gold-line mb-6" />

              <div className="space-y-4 text-[#2C2C2C]/80 leading-relaxed">
                <p>
                  A Quinta da Oliveirinha nasceu do desejo de regressar às origens
                  e de valorizar a riqueza da nossa terra. Localizados em Gomide,
                  Vila Verde, no coração do Minho, cultivamos uma variedade de
                  legumes e frutas seguindo os ensinamentos das gerações anteriores.
                </p>
                <p>
                  O nome Oliveirinha nasce, antes de mais, da nossa família: a
                  família Oliveira. É também uma referência carinhosa à Maria
                  Leonor, o elemento mais novo da família, a nossa pequena
                  Oliveirinha, que representa a continuidade, a esperança e o
                  futuro deste projeto.
                </p>
                <p>
                  Hoje, trabalhamos em família para levar até si o melhor da nossa
                  horta: produtos frescos, saborosos e cultivados com respeito pela
                  natureza. Cada cabaz que preparamos é uma extensão do nosso lar.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="image-hover rounded-xl overflow-hidden"
            >
              <img
                src={HANDS_IMAGE}
                alt="Mãos a colher produtos frescos"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 lg:py-28 bg-[#F3F1EB] noise-overlay">
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="font-accent text-2xl text-[#1B93A4]">
              A Nossa Filosofia
            </span>
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2">
              Os pilares da Quinta
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 gap-6"
          >
            {philosophy.map((item, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card
                  className="bg-white border-[#D4C4A8]/50 h-full card-hover"
                  data-testid={`philosophy-card-${index}`}
                >
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-[#3A4A38]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-7 h-7 text-[#3A4A38]" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-[#2C2C2C] mb-2">
                          {item.title}
                        </h3>
                        <p className="text-[#2C2C2C]/70 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1 image-hover rounded-xl overflow-hidden"
            >
              <img
                src={FAMILY_IMAGE}
                alt="Família a partilhar refeição"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <span className="font-accent text-2xl text-[#1B93A4]">
                Compromisso
              </span>
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2 mb-6">
                O nosso compromisso consigo
              </h2>
              <div className="gold-line mb-6" />

              <div className="space-y-4 text-[#2C2C2C]/80 leading-relaxed mb-8">
                <p>
                  Acreditamos que a comida boa começa na terra. Por isso, comprometemo-nos
                  a cultivar de forma responsável, sem químicos agressivos e respeitando
                  os tempos da natureza.
                </p>
                <p>
                  Cada produto que sai da nossa quinta é fruto de trabalho dedicado
                  e de práticas que promovem a saúde do solo e a qualidade dos alimentos.
                </p>
                <p>
                  Queremos que cada cabaz que recebe seja uma experiência de sabor
                  genuíno – legumes e frutas que têm o gosto do campo, da tradição
                  e do carinho com que são cultivados.
                </p>
              </div>

              <Link to="/produtos" data-testid="about-cta-products">
                <Button className="btn-primary">
                  Conhecer os nossos cabazes
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 lg:py-28 bg-[#F3F1EB] noise-overlay">
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="font-accent text-2xl text-[#1B93A4]">
              A Nossa Família
            </span>
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2">
              A Equipa da Quinta
            </h2>
            <p className="text-[#2C2C2C]/70 mt-4 max-w-2xl mx-auto">
              Somos uma família unida pelo amor à terra. Três gerações dedicadas 
              a cultivar produtos de qualidade para a sua mesa.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {teamMembers.map((member, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card
                  className="bg-white border-[#D4C4A8]/50 h-full card-hover overflow-hidden"
                  data-testid={`team-card-${index}`}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={member.image}
                      alt={member.name}
                      className={`w-full h-full object-cover transition-transform duration-500 hover:scale-105 ${member.imagePosition || 'object-center'}`}
                    />
                  </div>
                  <CardContent className="p-5 text-center">
                    <h3 className="text-lg font-semibold text-[#2C2C2C]">
                      {member.name}
                    </h3>
                    <span className="inline-block text-sm font-medium text-[#1B93A4] mb-2">
                      {member.role}
                    </span>
                    <p className="text-sm text-[#2C2C2C]/70 leading-relaxed">
                      {member.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#3A4A38] text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <span className="font-accent text-2xl text-[#D4C4A8]">
              Junte-se a nós
            </span>
            <h2 className="text-3xl lg:text-4xl font-semibold mt-2 mb-6">
              Faça parte da nossa comunidade
            </h2>
            <p className="text-white/80 mb-8">
              Estamos sempre disponíveis para responder às suas questões, receber
              visitas na quinta ou simplesmente partilhar a nossa paixão pela
              agricultura. Entre em contacto connosco!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contacto" data-testid="about-final-cta">
                <Button className="bg-white text-[#3A4A38] hover:bg-white/90 rounded-full px-8 py-6 text-base font-medium">
                  Entrar em Contacto
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
